import React, { useState, useEffect } from 'react';
import { FaEdit } from 'react-icons/fa';
import { toast } from 'react-toastify';
import useApi from '../../hooks/useApi';
import FormInput from '../common/FormInput';
import Button from '../common/Button';
import Alert from '../common/Alert';
import IconButton from '../common/IconButton';
import ThemeSwitcher from '../common/ThemeSwitcher';
import CommunityIdentity from '../common/CommunityIdentity';
import PrivacyDataRequests from './PrivacyDataRequests';

/**
 * User profile component
 */
function Profile() {
  const [user, setUser] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    mobileNo: '',
    email: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [privacyCenterVisible, setPrivacyCenterVisible] = useState(false);
  const { get, put } = useApi();

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await get('/api/users/current');
        console.log('Profile - Fetched user:', response);
        setUser(response);
        setFormData({
          name: response.name || '',
          mobileNo: response.mobileNo || '',
          email: response.email || '',
        });
        try {
          const visibility = await get('/api/community-settings/privacy-centre-visibility');
          setPrivacyCenterVisible(Boolean(visibility.visible));
        } catch (visibilityError) {
          console.error('Profile - Privacy Centre visibility error:', visibilityError);
          setPrivacyCenterVisible(false);
        }
        setError('');
      } catch (err) {
        console.error('Profile - Fetch error:', err);
        setError('Unable to load profile.');
        toast.error('Unable to load profile.');
      }
    };
    fetchProfile();
  }, [get]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      console.log('Profile - Submitting payload:', formData);
      const response = await put('/api/users/current', formData);
      console.log('Profile - Update response:', response);
      setUser({
        ...user,
        name: response.name,
        mobileNo: response.mobileNo,
        email: response.email,
      });
      setEditMode(false);
      setSuccess('Profile updated successfully!');
      toast.success('Profile updated.');
      setError('');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Profile - Update error:', err);
      setError(err.response?.data?.message || 'Failed to update profile.');
      toast.error('Failed to update profile.');
    }
  };

  const toggleEditMode = () => {
    setEditMode(!editMode);
    setError('');
    setSuccess('');
    if (!editMode && user) {
      setFormData({
        name: user.name || '',
        mobileNo: user.mobileNo || '',
        email: user.email || '',
      });
    }
  };

  if (!user) {
    return (
      <div className="container mt-5 animate__animated animate__fadeIn">
        <h2 className="text-center mb-4">Profile</h2>
        <Alert type="info" message="Loading..." />
      </div>
    );
  }

  return (
    <div className="container mt-5 animate__animated animate__fadeIn">
      <h2 className="text-center mb-4">Profile</h2>
      <div className="card shadow">
        <div className="card-body">
          <div className="profile-card-heading">
            <div><span>Account</span><h5 className="card-title">User Profile</h5></div>
            <div><small>Appearance</small><ThemeSwitcher /></div>
          </div>
          {error && <Alert type="danger" message={error} />}
          {success && <Alert type="success" message={success} />}
          {!editMode ? (
            <>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-bold">Name</label>
                  <p>{user.name || 'N/A'}</p>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-bold">Email</label>
                  <p>{user.email || 'N/A'}</p>
                </div>
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-bold">Mobile Number</label>
                  <p>{user.mobileNo || 'N/A'}</p>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-bold">House Number</label>
                  <p>{user.houseNo || 'N/A'}</p>
                </div>
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-bold">Division</label>
                  <p>{user.division || 'N/A'}</p>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-bold">Ownership Type</label>
                  <p>{user.ownershipType || 'N/A'}</p>
                </div>
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-bold">Community</label>
                  {user.community
                    ? <CommunityIdentity community={user.community} size="sm" />
                    : <p>N/A</p>}
                </div>
              </div>
              <IconButton
                icon={<FaEdit />}
                tooltip="Edit Profile"
                variant="primary"
                onClick={toggleEditMode}
                className="w-100"
              >
                Edit Profile
              </IconButton>
            </>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <FormInput
                    label="Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    maxLength={50}
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <FormInput
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <FormInput
                    label="Mobile Number"
                    name="mobileNo"
                    type="tel"
                    value={formData.mobileNo}
                    onChange={handleChange}
                    pattern="[0-9]{10}"
                    required
                    title="Mobile number must be 10 digits"
                  />
                </div>
              </div>
              <div className="d-flex gap-2">
                <Button type="submit" variant="primary" className="w-50">
                  Save Changes
                </Button>
                <Button type="button" variant="secondary" className="w-50" onClick={toggleEditMode}>
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
      {privacyCenterVisible && <PrivacyDataRequests />}
    </div>
  );
}

export default Profile;
